import { useAtom } from 'jotai'
import { match, P } from 'ts-pattern'
import { InvoiceDueDateField } from 'Invoices/components/InvoiceDueDateField/InvoiceDueDateField'
import { clientInvoiceAtom } from 'Invoices/atoms/invoiceEditing.atom'
import { useInvoiceDueDateTerms } from 'Invoices/hooks/useInvoiceDueDateTerms'
import { getOptimisticDueDate } from 'Invoices/utils'
import { DATE_FORMAT_DISPLAY } from 'Invoices/constants'
import type { DueDateBasis } from 'Invoices/types'
import { localDateWithFormat } from '@sequencehq/utils'
import { FC } from 'react'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'

type Props = {
  width: string
  onChange?: () => void
  'data-testid'?: string
  initiallyOpen?: boolean
}

export const CurrentInvoiceDueDatePicker: FC<Props> = ({
  width,
  onChange,
  'data-testid': dataTestid,
  initiallyOpen = false
}) => {
  const [clientInvoice, partialUpdateInvoice] = useAtom(clientInvoiceAtom)
  const { calculatedFrom } = useInvoiceDueDateTerms()

  const { data: defaultDueDateDays } = useQuery(
    dashboard20240730Client.getMerchantForSequenceAccount,
    undefined,
    { select: data => data?.defaultDueDateDays }
  )

  if (
    !clientInvoice ||
    typeof defaultDueDateDays !== 'number' ||
    !calculatedFrom
  ) {
    return null
  }

  const basis: DueDateBasis = match(calculatedFrom)
    .with('ACCOUNTING_DATE', calculatedFrom => ({
      calculatedFrom,
      accountingDate: new Date(clientInvoice.accountingDate)
    }))
    .with('ISSUE_DATE', calculatedFrom => ({ calculatedFrom }))
    .exhaustive()

  const selection = match({
    calculatedFrom,
    dueDatePolicy: clientInvoice.dueDatePolicy,
    dueDate: clientInvoice.dueDate
  })
    /**
     * A draft invoice created in accounting date due date mode, which is viewed in issue date mode,
     * will have a set dueDate and dueDatePolicy.paymentTermDays. The dueDate takes precedence when issuing the invoice,
     * so this workaround prevents the paymentTermDays from appearing as if it's been selected in the dropdown.
     */
    .with(
      {
        calculatedFrom: 'ISSUE_DATE',
        dueDatePolicy: { type: 'PAYMENT_TERMS' },
        dueDate: P.string.minLength(1)
      },
      ({ dueDatePolicy, dueDate }) => ({
        value: undefined,
        fallbackTermDays: dueDatePolicy.paymentTermDays,
        placeholder: localDateWithFormat(dueDate, DATE_FORMAT_DISPLAY)
      })
    )
    .otherwise(({ dueDatePolicy }) => ({
      value: dueDatePolicy ?? undefined,
      // Legacy invoices can carry their own terms with no policy at all
      fallbackTermDays: clientInvoice.paymentTermDays ?? defaultDueDateDays,
      placeholder: undefined
    }))

  return (
    <InvoiceDueDateField
      {...selection}
      onChange={policy => {
        // The due date itself is derived by the backend, so drop the stale one
        // and let the resolver re-derive it until the save returns.
        partialUpdateInvoice({
          dueDatePolicy: policy,
          ...getOptimisticDueDate(policy)
        })
        onChange?.()
      }}
      basis={basis}
      defaultDueDays={defaultDueDateDays}
      width={width}
      data-testid={dataTestid}
      initiallyOpen={initiallyOpen}
    />
  )
}
